/**
 * mcp-connector.ts — Hàm thuần cho "Kết nối AI (Claude)" (MCP connector, docs/THIET-KE-MCP-CONNECTOR.md):
 * màn đồng ý `/oauth/consent` và mục Cài đặt → Kết nối AI. Không gọi API, không JSX — để test được.
 */
import { i18n } from '@/lib/i18n'

export type McpScopeKey = 'ecdp.read' | 'ecdp.write' | 'ecdp.send' | 'ecdp.delete' | 'ecdp.pii'

/** Quyền ứng dụng xin, kèm tổ chức có cho phép hay không (GET /mcp/oauth/request). */
export interface McpScopeRequest {
  key: McpScopeKey
  label: string
  allowed: boolean
}

/** `ecdp.read` luôn cấp (không có nó thì connector vô dụng) — ô của nó tích sẵn và khoá. */
export const MCP_BASE_SCOPE: McpScopeKey = 'ecdp.read'

/** Nhãn ngắn của từng quyền — dùng cho huy hiệu ở bảng kết nối. Quyền lạ → giữ nguyên khoá. */
const SCOPE_SHORT_LABEL_KEYS: Record<string, string> = {
  'ecdp.read': 'read',
  'ecdp.write': 'write',
  'ecdp.send': 'send',
  'ecdp.delete': 'delete',
  'ecdp.pii': 'pii',
}

export function mcpScopeShortLabel(key: string): string {
  const k = SCOPE_SHORT_LABEL_KEYS[key]
  return k ? i18n.t(`sharedMisc:mcpConnector.scope.${k}`) : key
}

/**
 * Quyền rủi ro cao — KHÔNG tích sẵn ở màn đồng ý (người dùng phải tự tích): gửi tin thật + chiến dịch, xoá dữ liệu. Tách khỏi `ecdp.write`
 * để cấp quyền huấn luyện bot không kéo theo gửi tin hay xoá.
 */
export const MCP_OPT_IN_SCOPES: readonly McpScopeKey[] = ['ecdp.send', 'ecdp.delete']

/**
 * Lựa chọn ban đầu ở màn đồng ý: `ecdp.read` + mọi quyền tổ chức cho phép mà ứng dụng xin, TRỪ quyền rủi ro cao (MCP_OPT_IN_SCOPES).
 * Người dùng bỏ tích được các quyền thêm; quyền tổ chức chưa cho phép không bao giờ được chọn.
 */
export function initialMcpScopes(scopes: McpScopeRequest[]): McpScopeKey[] {
  const keys = new Set<McpScopeKey>([MCP_BASE_SCOPE])
  for (const s of scopes) if (s.allowed && !MCP_OPT_IN_SCOPES.includes(s.key)) keys.add(s.key)
  return [...keys]
}

/** Đổi tích một quyền — bỏ qua `ecdp.read` và quyền tổ chức chưa cho phép. */
export function toggleMcpScope(selected: McpScopeKey[], scope: McpScopeRequest, on: boolean): McpScopeKey[] {
  if (scope.key === MCP_BASE_SCOPE || !scope.allowed) return selected
  const rest = selected.filter((k) => k !== scope.key)
  return on ? [...rest, scope.key] : rest
}

/**
 * Kiểu ứng dụng đang xin quyền: Claude chính chủ (Anthropic) → huy hiệu xanh; ứng dụng chạy trên máy người dùng
 * (redirect về localhost, vd Claude Code) → cảnh báo nhẹ "chỉ cho phép nếu chính bạn vừa thêm"; còn lại → cảnh báo "chỉ cho phép nếu tin".
 */
export type McpClientKind = 'official' | 'local' | 'unknown'

export function mcpClientKind(client: { official: boolean; local: boolean }): McpClientKind {
  if (client.official) return 'official'
  if (client.local) return 'local'
  return 'unknown'
}
